import type { WireConnections } from '@/api/wire';
import { localIsoDate } from '@/today/dates';

/**
 * How long a grant of these tools may run, read the way core-api's
 * `termFor` reads it (ADR-0085): each tool takes its own term, else the
 * shortest of its connections', else the platform's; the grant takes the
 * shortest among its tools. The form says it before the token is minted, so
 * a person never learns the limit from a refusal. core-api still decides.
 */
export function grantTermDays(tools: readonly string[], data: Pick<WireConnections, 'connections' | 'terms' | 'platformTermDays'>): number {
  const platform = data.platformTermDays;
  if (tools.length === 0) return platform;
  const own = (level: 'tool' | 'connection', ref: string) => data.terms.find((t) => t.level === level && t.ref === ref)?.days;
  return Math.min(
    ...tools.map((tool) => {
      const mine = own('tool', tool);
      if (mine !== undefined) return mine;
      const byConnection = data.connections
        .filter((c) => c.tools.includes(tool))
        .map((c) => own('connection', c.ref))
        .filter((d): d is number => d !== undefined);
      return byConnection.length > 0 ? Math.min(...byConnection) : platform;
    }),
  );
}

const DAY_MS = 86_400_000;

/** The date `days` from `now`, as the date field holds it. */
export function dateIn(days: number, now = Date.now()): string {
  return localIsoDate(new Date(now + days * DAY_MS));
}

/**
 * The instant a grant ending on `date` is sent with: the end of that day,
 * but never past the last instant the term allows. core-api measures the
 * term from now, so the end of its last day would be up to a day over and
 * refused; the last allowed day is still offered, and ends when the term does.
 */
export function endsAt(date: string, limitDays: number | null, now = Date.now()): Date {
  const endOfDay = Date.parse(`${date}T23:59:59Z`);
  return new Date(limitDays === null ? endOfDay : Math.min(endOfDay, now + limitDays * DAY_MS));
}

/** Whether `date` is within the term, compared as days so the last day counts. */
export function withinTerm(date: string, limitDays: number | null, now = Date.now()): boolean {
  return limitDays === null || date <= dateIn(limitDays, now);
}

/** The quick choices, each only when the term reaches it; the longest is always offered. */
export function endChoices(limitDays: number | null): { label: string; days: number }[] {
  const steps = [30, 60, 90, 180, 365];
  if (limitDays === null) return steps.slice(0, 3).map((d) => ({ label: `${d} days`, days: d }));
  const under = steps.filter((d) => d < limitDays).map((d) => ({ label: `${d} days`, days: d }));
  return [...under, { label: `Longest allowed (${limitDays} days)`, days: limitDays }];
}
